/**
 * One case, as tabs.
 *
 * The tab order is the order an analyst works in: what was collected, what it means,
 * what needs deciding, and what the evidence actually says. Review carries a count so a
 * pending decision is visible without opening the tab — the human gate only works if
 * people look at it.
 */

import { useState } from "react";
import type { ReactElement } from "react";
import {
  NavLink,
  Navigate,
  Route,
  Routes,
  useNavigate,
  useParams,
} from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { request } from "@/lib/api";
import type { CaseCounts, CaseResponse } from "@/lib/schemas";
import { Badge, Button, ErrorState, Loading } from "@/components/ui";
import { useCan } from "@/hooks/usePermissions";
import { ResultsTab } from "@/features/results/ResultsTab";
import { ObservationsTab } from "@/features/results/ObservationsTab";
import { GraphTab } from "@/features/graph/GraphTab";
import { EntitiesTab } from "@/features/entities/EntitiesTab";
import { TimelineTab } from "@/features/timeline/TimelineTab";
import { RunsTab } from "@/features/runs/RunsTab";
import { ReviewQueue } from "@/features/review/ReviewPage";
import { EvidencePage } from "@/features/evidence/EvidencePage";
import { ReportsTab } from "@/features/reports/ReportsTab";
import { AiTab } from "@/features/ai/AiTab";
import { NotesPanel } from "@/features/workspace/NotesPanel";
import { SubjectsTab } from "@/features/cases/SubjectsTab";
import type { PromoteCaseRequest } from '@/lib/schemas';

const TABS = [
  { path: "subjects", label: "Subjects" },
  { path: "runs", label: "Runs" },
  { path: "results", label: "Results" },
  { path: "observations", label: "Observations" },
  { path: "entities", label: "Entities" },
  { path: "graph", label: "Graph" },
  { path: "timeline", label: "Timeline" },
  { path: "review", label: "Review" },
  { path: "ai", label: "Synthesis" },
  { path: "reports", label: "Reports" },
  { path: "notes", label: "Notes" },
  { path: "evidence", label: "Evidence" },
];

/** The case workspace. */
export function CaseWorkspace(): ReactElement {
  const { caseId } = useParams<{ caseId: string }>();

  const detail = useQuery({
    queryKey: ["case", caseId],
    queryFn: () => request<CaseResponse>(`/cases/${caseId}`),
    enabled: Boolean(caseId),
  });
  const counts = useQuery({
    queryKey: ["case-counts", caseId],
    queryFn: () => request<CaseCounts>(`/cases/${caseId}/counts`),
    enabled: Boolean(caseId),
  });

  if (!caseId) return <Navigate to="/cases" replace />;
  if (detail.isLoading) return <Loading />;
  if (detail.error) return <ErrorState error={detail.error} />;

  const outstanding =
    (counts.data?.pending_proposals ?? 0) +
    (counts.data?.unconfirmed_hypotheses ?? 0);

  return (
    <div className="space-y-4">
      <header className="space-y-1">
        <div className="flex flex-wrap items-center gap-3">
          <h1 className="text-lg font-semibold">{detail.data?.title}</h1>
          <Badge tone="accent">{detail.data?.investigation_purpose}</Badge>
          <span className="font-mono text-xs text-muted">
            {detail.data?.reference}
          </span>
          {detail.data && (
            <Lifecycle
              caseId={caseId}
              status={detail.data.status}
              reference={detail.data.reference}
              kind={detail.data.kind}
            />
          )}
        </div>
        <p className="max-w-3xl text-xs text-muted">
          {detail.data?.authorization_attestation}
        </p>
      </header>

      <nav
        aria-label="Case sections"
        className="flex gap-1 border-b border-line"
      >
        {TABS.map((tab) => (
          <NavLink
            key={tab.path}
            to={tab.path}
            className={({ isActive }) =>
              `flex items-center gap-2 border-b-2 px-3 py-2 text-sm ${
                isActive
                  ? "border-accent text-accent"
                  : "border-transparent text-muted"
              }`
            }
          >
            {tab.label}
            {tab.path === "review" && outstanding > 0 && (
              <Badge tone="warn">{outstanding}</Badge>
            )}
          </NavLink>
        ))}
      </nav>

      <Routes>
        <Route index element={<Navigate to="runs" replace />} />
        <Route path="subjects" element={<SubjectsTab caseId={caseId} />} />
        <Route path="runs" element={<RunsTab caseId={caseId} />} />
        <Route path="results" element={<ResultsTab caseId={caseId} />} />
        <Route
          path="observations"
          element={<ObservationsTab caseId={caseId} />}
        />
        <Route path="entities" element={<EntitiesTab caseId={caseId} />} />
        <Route path="graph" element={<GraphTab caseId={caseId} />} />
        <Route path="timeline" element={<TimelineTab caseId={caseId} />} />
        <Route path="review" element={<ReviewQueue caseId={caseId} />} />
        <Route path="ai" element={<AiTab caseId={caseId} />} />
        <Route path="reports" element={<ReportsTab caseId={caseId} />} />
        <Route path="notes" element={<NotesPanel caseId={caseId} />} />
        <Route path="evidence" element={<EvidencePage caseId={caseId} />} />
      </Routes>
    </div>
  );
}

/**
 * The case's lifecycle state, and the controls that move it.
 *
 * **Every case used to be `draft` for ever.** `complete` and `archived` were modelled and
 * assigned by nothing, so a caseload could only grow and work finished two years ago
 * looked exactly like work in progress.
 *
 * `running` and `review` are not offered: the run already carries that state, and a case
 * status duplicating it would be a second source of truth free to disagree with the first.
 *
 * @param props - The case and its current status.
 * @returns The badge and its controls.
 */
export function Lifecycle({
  caseId,
  status,
  reference,
  kind = "investigation",
}: {
  caseId: string;
  status: string;
  reference: string;
  kind?: string;
}): ReactElement {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [confirming, setConfirming] = useState(false);
  const canWork = useCan("create_run_cases");
  // Archiving retires a record from everybody's working set, so it is owner/admin rather
  // than anyone who can run a case (ADR-0036).
  const canArchive = useCan("manage_tenant_settings");
  // Deleting removes the case from every colleague's working set; owner and admin hold it.
  // The button is not rendered at all for anyone else, because offering an action that will
  // be refused is a worse experience than not offering it.
  const canDelete = useCan("delete_cases");

  // The three paths are written out rather than built by interpolating the verb. A
  // constructed path is invisible to the surface-coverage check, which means a typo in
  // the action would ship as a 404 nobody could see coming.
  // **Promotion changes presentation, never the attestation.** The purpose, legal basis
  // and authorisation recorded when the session was opened are immutable at the database,
  // so a promoted record keeps the answers somebody gave before the work began.
  const promote = useMutation({
    mutationFn: (title: string) =>
      request<unknown, PromoteCaseRequest>(`/cases/${caseId}:promote`, { method: "POST", body: { title } }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["case", caseId] });
      void queryClient.invalidateQueries({ queryKey: ["cases"] });
    },
  });

  const move = useMutation({
    mutationFn: (action: "complete" | "archive" | "reopen") => {
      const path =
        action === "complete"
          ? `/cases/${caseId}:complete`
          : action === "archive"
            ? `/cases/${caseId}:archive`
            : `/cases/${caseId}:reopen`;
      return request<unknown>(path, { method: "POST" });
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["case", caseId] });
      void queryClient.invalidateQueries({ queryKey: ["cases"] });
    },
  });

  // **Deletion navigates away**, because staying on a case that no longer exists would
  // leave the next refetch answering 404 and the screen showing an error for something
  // that worked exactly as asked.
  const remove = useMutation({
    mutationFn: () =>
      request<unknown>(`/cases/${caseId}`, { method: "DELETE" }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["cases"] });
      queryClient.removeQueries({ queryKey: ["case", caseId] });
      void navigate("/cases");
    },
  });

  const tone =
    status === "archived" ? "muted" : status === "complete" ? "ok" : "accent";
  const open =
    status === "draft" || status === "running" || status === "review";

  return (
    <span className="flex flex-wrap items-center gap-2">
      <Badge tone={tone}>{status}</Badge>
      {kind === "research_session" && (
        <Badge tone="muted">research session</Badge>
      )}
      {canWork && kind === "research_session" && (
        <Button
          data-testid="promote-case"
          disabled={promote.isPending}
          onClick={() => {
            const title = window.prompt(
              "Name the investigation. The purpose, legal basis and authorisation recorded when this session was opened do not change — promotion changes how the record is presented, not what anybody attested.",
              reference,
            );
            if (title != null && title.trim() !== "") promote.mutate(title.trim());
          }}
        >
          Promote to investigation
        </Button>
      )}
      {canWork && open && (
        <Button
          data-testid="complete-case"
          disabled={move.isPending}
          onClick={() => move.mutate("complete")}
        >
          Mark complete
        </Button>
      )}
      {canArchive && status !== "archived" && (
        <Button
          data-testid="archive-case"
          disabled={move.isPending}
          onClick={() => move.mutate("archive")}
        >
          Archive
        </Button>
      )}
      {canWork && !open && (
        <Button
          data-testid="reopen-case"
          disabled={move.isPending}
          onClick={() => move.mutate("reopen")}
        >
          Reopen
        </Button>
      )}
      {canDelete &&
        (confirming ? (
          // **Two clicks, and the second one names the case.** A single Delete button next
          // to Archive is a button somebody presses meaning the other one.
          <span className="flex items-center gap-2 text-xs">
            <span className="text-muted">
              Delete <span className="font-mono">{reference}</span> for
              everyone?
            </span>
            <Button
              data-testid="confirm-delete-case"
              disabled={remove.isPending}
              onClick={() => remove.mutate()}
            >
              {remove.isPending ? "Deleting…" : "Yes, delete"}
            </Button>
            <Button
              data-testid="cancel-delete-case"
              onClick={() => setConfirming(false)}
            >
              Cancel
            </Button>
          </span>
        ) : (
          <Button data-testid="delete-case" onClick={() => setConfirming(true)}>
            Delete
          </Button>
        ))}
      {move.error != null && <ErrorState error={move.error} />}
      {remove.error != null && <ErrorState error={remove.error} />}
    </span>
  );
}
